import type { GraphData, ChartType, MetricType, METRIC_METADATA } from '@/components/reports/graphs/types';

/**
 * Transform analytics API response to Recharts-compatible format
 */
export function transformAnalyticsData(data: GraphData, chartType: ChartType): any[] {
  if (!data || !data.periods || data.periods.length === 0) {
    return [];
  }

  return data.periods.map(period => {
    const baseData = {
      period: period.period,
      total: period.total,
      average: period.average,
      median: period.median,
      min: period.min,
      max: period.max,
    };

    // Include breakdown data if present
    if (period.breakdown) {
      if (period.breakdown.by_category) {
        (baseData as any).categories = period.breakdown.by_category;
      }
      if (period.breakdown.by_employees) {
        (baseData as any).employees = period.breakdown.by_employees;
      }
    }

    return baseData;
  });
}

/**
 * Get the description for a metric type
 */
export function getMetricDescription(metric: MetricType): string {
  const metadata: Record<MetricType, { label: string; description: string; yAxisLabel: string }> = {
    addons_per_page: {
      label: 'Add-ons per Page',
      description: 'Count of add-on plans per pricing page',
      yAxisLabel: 'Number of Add-ons'
    },
    modalities_per_company: {
      label: 'Modalities per Company',
      description: 'Count of unique pricing modalities per company',
      yAxisLabel: 'Number of Modalities'
    },
    thresholds_per_plan: {
      label: 'Thresholds per Plan',
      description: 'Average number of pricing thresholds per plan',
      yAxisLabel: 'Number of Thresholds'
    },
    company_count: {
      label: 'Company Count',
      description: 'Total number of companies tracked',
      yAxisLabel: 'Number of Companies'
    }
  };

  return metadata[metric].description;
}

/**
 * Get the display label for a metric type
 */
export function getMetricLabel(metric: MetricType): string {
  const metadata: Record<MetricType, { label: string; description: string; yAxisLabel: string }> = {
    addons_per_page: {
      label: 'Add-ons per Page',
      description: 'Count of add-on plans per pricing page',
      yAxisLabel: 'Number of Add-ons'
    },
    modalities_per_company: {
      label: 'Modalities per Company',
      description: 'Count of unique pricing modalities per company',
      yAxisLabel: 'Number of Modalities'
    },
    thresholds_per_plan: {
      label: 'Thresholds per Plan',
      description: 'Average number of pricing thresholds per plan',
      yAxisLabel: 'Number of Thresholds'
    },
    company_count: {
      label: 'Company Count',
      description: 'Total number of companies tracked',
      yAxisLabel: 'Number of Companies'
    }
  };

  return metadata[metric].label;
}

/**
 * Get the Y-axis label for a metric type
 */
export function getYAxisLabel(metric: MetricType): string {
  const metadata: Record<MetricType, { label: string; description: string; yAxisLabel: string }> = {
    addons_per_page: {
      label: 'Add-ons per Page',
      description: 'Count of add-on plans per pricing page',
      yAxisLabel: 'Number of Add-ons'
    },
    modalities_per_company: {
      label: 'Modalities per Company',
      description: 'Count of unique pricing modalities per company',
      yAxisLabel: 'Number of Modalities'
    },
    thresholds_per_plan: {
      label: 'Thresholds per Plan',
      description: 'Average number of pricing thresholds per plan',
      yAxisLabel: 'Number of Thresholds'
    },
    company_count: {
      label: 'Company Count',
      description: 'Total number of companies tracked',
      yAxisLabel: 'Number of Companies'
    }
  };

  return metadata[metric].yAxisLabel;
}

/**
 * Generate array of recent quarters for period selection
 * Starts from the previous quarter (not current) to avoid incomplete data
 */
export function getRecentQuarters(count: number = 9): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  const quarters: string[] = [];

  // Start from i=1 to skip current quarter and begin with previous quarter
  for (let i = 1; i <= count; i++) {
    let q = currentQuarter - i;
    let y = currentYear;

    while (q <= 0) {
      q += 4;
      y -= 1;
    }

    quarters.push(`${y}Q${q}`);  // Format: 2025Q4 (no dash)
  }

  return quarters;
}

/**
 * Generate array of recent years for period selection (yearly granularity)
 * Returns years in format: 2026Y, 2025Y, etc.
 */
export function getRecentYears(count: number = 5): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];

  for (let i = 0; i < count; i++) {
    years.push(`${currentYear - i}Y`);  // Format: 2025Y
  }

  return years;
}

/**
 * Validate period format (YYYYQ#)
 */
export function isValidPeriodFormat(period: string): boolean {
  const regex = /^\d{4}Q[1-4]$/;  // Updated to match YYYYQ# format
  return regex.test(period);
}

/**
 * Format statistics for display
 */
export function formatStatistic(value: number, decimals: number = 1): string {
  return value.toFixed(decimals);
}
